/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { useIntl } from 'react-intl'

import type { EuiDescriptionListProps } from '@elastic/eui'
import { EuiDescriptionList, EuiHorizontalRule, EuiPanel, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { LineItem } from '@modules/billing-api/customTypes'
import { useConfig } from '@modules/cui/ConfigContext'
import CuiPrice from '@modules/cui/formatters/CuiPrice'

const PricesSection = ({ lineItem }: { lineItem: LineItem }) => {
  const { formatMessage } = useIntl()
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  if (lineItem.external_reseller_id) {
    return null
  }

  const listItems = [
    {
      title: formatMessage({
        id: 'line-item.details-flyout.prices.paid-amount',
        defaultMessage: 'Paid amount',
      }),
      isVisible: true,
      description: (
        <CuiPrice
          data-test-id='purchased-price'
          value={lineItem.purchased_balance || 0}
          unit='none'
          currency={lineItem.currency}
        />
      ),
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.prices.price_at_purchase',
        defaultMessage: 'List price at purchase',
      }),
      isVisible: true,
      description: (
        <CuiPrice
          data-test-id='purchased-price'
          value={lineItem.ecu_price || 0}
          unit='none'
          currency={lineItem.currency}
        />
      ),
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.prices.discount',
        defaultMessage: 'Discount rate',
      }),
      isVisible: true,
      description: `${lineItem.discount}%`,
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.prices.prepaid_charge',
        defaultMessage: 'Prepaid charge',
      }),
      isVisible: !!lineItem.ecu_prepaid_charge && isAdminConsole,
      description: (
        <CuiPrice
          data-test-id='purchased-price'
          value={lineItem.ecu_prepaid_charge || 0}
          unit='none'
          currency={lineItem.currency}
        />
      ),
    },
  ].filter((item) => item.isVisible)

  return (
    <Fragment>
      <EuiHorizontalRule margin='xs' />
      <EuiPanel paddingSize='m' hasBorder={false} hasShadow={false}>
        <EuiText size='s'>
          <h4>
            <FormattedMessage
              id='line-item.details-flyout.prices.title'
              defaultMessage='Prices and charges'
            />
          </h4>
        </EuiText>
        <EuiSpacer />
        <EuiDescriptionList
          type='column'
          compressed={true}
          columnGutterSize='m'
          listItems={listItems as EuiDescriptionListProps['listItems']}
        />
      </EuiPanel>
    </Fragment>
  )
}

export default PricesSection
